import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  type Document,
  type DocumentType,
  type DocumentLineItem,
} from '@/hooks/useDocumentsWorkItems';
import {
  type FinanceListRequest,
  type DocumentFormState,
} from '../documentModalHelpers';
import { authService } from '@/lib/authService';
import type { Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';

const DOC_TYPE_LABELS: Record<string, string> = {
  quote: 'Quote',
  proforma: 'Proforma',
  invoice: 'Invoice',
  sales_order: 'Sales Order',
  purchase_order: 'Purchase Order',
  expense: 'Expense',
  supplier_quote: 'Supplier Quote',
  grn: 'GRN',
  delivery_note: 'Delivery Note',
  credit_note: 'Credit Note',
  supplier_credit: 'Supplier Credit',
  rfq: 'RFQ',
  purchase_requisition: 'Purchase Requisition',
  billing_submission: 'Billing Submission',
  din: 'DIN',
};

function getUpstreamDocumentTypes(docType: DocumentType): DocumentType[] {
  switch (docType) {
    case 'invoice':
      return ['quote', 'proforma', 'sales_order'];
    case 'proforma':
      return ['quote'];
    case 'sales_order':
      return ['quote'];
    case 'expense':
      return ['purchase_order', 'supplier_quote'];
    case 'grn':
      return ['purchase_order'];
    case 'purchase_order':
      return ['rfq', 'supplier_quote', 'purchase_requisition'];
    case 'supplier_quote':
      return ['rfq'];
    case 'delivery_note':
      return ['invoice', 'sales_order'];
    case 'credit_note':
      return ['invoice'];
    case 'supplier_credit':
      return ['expense'];
    default:
      return [];
  }
}

interface UseParentDocumentsParams {
  doc_type: DocumentType;
  contactId: number | undefined;
  contacts: Contact[];
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  setItems: React.Dispatch<React.SetStateAction<DocumentLineItem[]>>;
  setSelectedContact: React.Dispatch<React.SetStateAction<Contact | null>>;
  loadDocumentItems: (id: string) => Promise<{ data: DocumentLineItem[] }>;
}

export function useParentDocuments({
  doc_type,
  contactId,
  contacts,
  setFormData,
  setItems,
  setSelectedContact,
  loadDocumentItems,
}: UseParentDocumentsParams) {
  const [showParentSearch, setShowParentSearch] = useState(false);
  const [parentDocumentSearchQuery, setParentDocumentSearchQuery] = useState('');
  const [parentDocumentResults, setParentDocumentResults] = useState<Document[]>([]);
  const [searchingParent, setSearchingParent] = useState(false);
  const [linkedParentDocument, setLinkedParentDocument] = useState<Document | null>(null);

  const upstreamDocTypes = getUpstreamDocumentTypes(doc_type);
  const canLinkParent = upstreamDocTypes.length > 0;
  const docTypeLabels = DOC_TYPE_LABELS;

  const loadParentDocuments = useCallback(
    async (searchQuery?: string) => {
      if (upstreamDocTypes.length === 0) {
        setParentDocumentResults([]);
        return;
      }

      if (!contactId) {
        setParentDocumentResults([]);
        return;
      }

      setSearchingParent(true);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) return;

        const requestBody: FinanceListRequest = {
          action: 'list',
          table: 'work_items',
          where: {
            item_type: { operator: 'in', value: upstreamDocTypes },
            status: { operator: 'in', value: ['draft', 'sent', 'accepted', 'paid', 'viewed'] },
            contact_id: contactId,
          },
          orderBy: [{ field: 'created_at', direction: 'desc' }],
          limit: 50,
        };

        if (searchQuery?.trim()) {
          requestBody.search = searchQuery;
          requestBody.searchFields = ['item_number', 'doc_number', 'contact_name', 'title', 'notes'];
        }

        const response = await fetch('/api/v1/finance', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setParentDocumentResults(result.data);
          }
        }
      } catch (error) {
        console.error('Error loading parent documents:', error);
      } finally {
        setSearchingParent(false);
      }
    },
    [upstreamDocTypes, contactId],
  );

  const searchParentDocuments = useCallback(
    (query: string) => loadParentDocuments(query),
    [loadParentDocuments],
  );

  const handleSelectParentDocument = useCallback(
    async (parentDoc: Document) => {
      setLinkedParentDocument(parentDoc);
      setShowParentSearch(false);
      setParentDocumentSearchQuery('');
      setParentDocumentResults([]);

      setFormData((prev) => ({
        ...prev,
        parent_document_id: parentDoc.id,
        contact_id: parentDoc.contact_id,
        contact_name: parentDoc.contact_name,
        contact_email: parentDoc.contact_email,
        reference: parentDoc.doc_number
          ? `From ${docTypeLabels[parentDoc.doc_type || '']} ${parentDoc.doc_number}`
          : prev.reference,
      }));

      if (parentDoc.id) {
        const itemsResult = await loadDocumentItems(parentDoc.id);
        if (itemsResult && itemsResult.data && itemsResult.data.length > 0) {
          const copiedItems = itemsResult.data.map((item: DocumentLineItem) => ({
            ...item,
            id: undefined,
            document_id: undefined,
          }));
          setItems(copiedItems);
          toast.success(
            `Copied ${copiedItems.length} items from ${docTypeLabels[parentDoc.doc_type || '']}`,
          );
        }
      }

      if (parentDoc.contact_id && contacts.length > 0) {
        const contact = contacts.find((c) => c.id === parentDoc.contact_id);
        if (contact) {
          setSelectedContact(contact);
        }
      }
    },
    [setFormData, setItems, setSelectedContact, loadDocumentItems, contacts, docTypeLabels],
  );

  const handleClearParentDocument = useCallback(() => {
    setLinkedParentDocument(null);
    setFormData((prev) => ({
      ...prev,
      parent_document_id: undefined,
    }));
  }, [setFormData]);

  return {
    showParentSearch,
    setShowParentSearch,
    parentDocumentSearchQuery,
    setParentDocumentSearchQuery,
    parentDocumentResults,
    setParentDocumentResults,
    searchingParent,
    linkedParentDocument,
    upstreamDocTypes,
    canLinkParent,
    docTypeLabels,
    loadParentDocuments,
    searchParentDocuments,
    handleSelectParentDocument,
    handleClearParentDocument,
  };
}
